import React from 'react';
import PropTypes from 'prop-types';
import {
  exportPermission,
  hasPermission,
  hasSomePermission, manageSurveyPermission, participantsReadPermission, researchUserPermission
} from 'unihow-common';
import styles from './actionButtonGroup.module.css';
import { surveyTypeValues } from '../../../constants/types';
import CloseSurveyIconButton from '../../IconButtons/CloseSurveyIconButton';
import EditSurveyIconButton from '../../IconButtons/EditSurveyIconButton';
import SendReminderIconButton from '../../IconButtons/SendReminderIconButton';
import SchedulingIconButton from '../../IconButtons/SchedulingIconButton';
import ReopenSurveyIconButton from '../../IconButtons/ReopenSurveyIconButton';
import ViewParticipantsIconButton from '../../IconButtons/ViewParticipantsIconButton';
import {
  getSubRouteForSurveyType
} from '../../../util';
import ExcelExportButton from '../../IconButtons/ExportIconButton';
import useCurrentUser from '../../../hooks/useCurrentUser';

const ActionButtonGroup = ({
  surveySchedule,
  surveyInfo,
  surveyId
}) => {
  const { permissions: userPermissions } = useCurrentUser();
  const { type: surveyType } = surveyInfo;
  const isHUS = surveyType === surveyTypeValues.COURSE_SURVEY;
  const surveyTypeSubRoute = getSubRouteForSurveyType(surveyInfo.type);

  const viewParticipants = hasSomePermission(userPermissions, [
    manageSurveyPermission(surveyType),
    participantsReadPermission(surveyType)
  ]);

  const excelExport = hasSomePermission(userPermissions, [
    manageSurveyPermission(surveyType),
    exportPermission(surveyType),
    researchUserPermission(surveyType)
  ]);

  const isAdminForSurvey = hasPermission(userPermissions, manageSurveyPermission(surveyType));

  return (
    <div className={styles.container}>
      {(isAdminForSurvey && !isHUS) && (
        <>
          <SendReminderIconButton
            route={surveyTypeSubRoute.survey.reminder}
            surveyId={surveyId}
            disabled={surveySchedule.ended || !surveySchedule.started}
          />
          <SchedulingIconButton
            route={surveyTypeSubRoute.survey.reschedule}
            surveyId={surveyId}
            disabled={surveySchedule.ended || !surveySchedule.started}
          />
          <ReopenSurveyIconButton
            route={surveyTypeSubRoute.survey.reopen}
            surveyId={surveyId}
            disabled={!(surveySchedule.ended || (surveyInfo.hasBeenOpened && !surveySchedule.started))}
          />
        </>
      )}
      {(viewParticipants && !isHUS) && (
        <ViewParticipantsIconButton
          route={surveyTypeSubRoute.survey.participants.index}
          surveyId={surveyId}
        />
      )}
      {(isHUS || isAdminForSurvey) && (
        <>
          <CloseSurveyIconButton
            route={surveyTypeSubRoute.survey.close}
            surveyId={surveyId}
            disabled={surveySchedule.ended}
          />
          <EditSurveyIconButton
            route={surveyTypeSubRoute.survey.edit.contents.index}
            surveyId={surveyId}
            disabled={surveySchedule.started || surveyInfo.hasBeenOpened}
          />
        </>
      )}
      {(excelExport) && (
        <ExcelExportButton
          surveyId={surveyId}
          surveySchedule={surveySchedule}
          surveyInfo={surveyInfo}
        />
      )}
    </div>
  );
};

const {
  string, shape
} = PropTypes;

ActionButtonGroup.propTypes = {
  surveyInfo: shape(),
  surveySchedule: shape(),
  surveyId: string.isRequired
};

ActionButtonGroup.defaultProps = {
  surveyInfo: null,
  surveySchedule: null
};

export default ActionButtonGroup;
